// src/pages/common/SelectStoreAndInvoiceModal.tsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore } from '@/icons';
import { apiGet } from '../../services/api';
import SaveButton from '../../components/common/SaveButton';
import { Loja } from '../../types/parametrizacao/Loja';
import { useAuth } from '../../contexts/AuthContext';
import './SelectStoreAndInvoiceModal.css'; // Novo CSS, similar a SearchSupplierModalContent.css
import { logger } from '@/utils/logger';

interface SelectStoreAndInvoiceModalProps {
  theme: 'dark' | 'light';
  onSave: (storeId: number, storeName: string, invoice: string, tipo: string) => void; // Adicionado tipo
  onClose: () => void;
}

const SelectStoreAndInvoiceModal: React.FC<SelectStoreAndInvoiceModalProps> = ({
  theme,
  onSave,
  onClose: _onClose,
}) => {
  const { userId } = useAuth();
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [selectedLojaId, setSelectedLojaId] = useState<number | null>(null);
  const [selectedLojaName, setSelectedLojaName] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const tipoRecebimento = 'Ordem de Compra';
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
      const fetchLojas = async () => {
        if (!userId) return; // Evita chamada sem ID
        try {
          const res = await apiGet(`/v1/lojas-acesso/usuario/${userId}?paged=false`);
          // Só lojas ATIVAS podem receber (inativa → 400 no backend). O endpoint
          // /usuario/{id} retorna inativas (histórico) — filtramos aqui no seletor de
          // recebimento. Vale inclusive para ROLE_MASTER. (mesmo padrão do modal de fornecedor)
          setLojas(
            (res.data as Loja[])
              .filter((loja) => loja.ativo === true)
              .sort((a, b) => a.nomeFantasia.localeCompare(b.nomeFantasia))
          );
        } catch (err) {
          logger.error('Erro ao buscar lojas:', err);
        }
      };
      fetchLojas();
  }, [userId]); // Adicionado userId como dependência

  const handleLojaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    const loja = lojas.find((l) => l.id === id);
    setSelectedLojaId(id);
    setSelectedLojaName(loja?.nomeFantasia || '');
  };

  const handleSave = async () => {
    if (!selectedLojaId || !invoiceNumber) return;
    setIsSaving(true);
    onSave(selectedLojaId, selectedLojaName, invoiceNumber, tipoRecebimento); // Adicionado tipo
    setIsSaving(false);
  };

  return (
    <div className={`select-store-invoice-modal-content ${theme}`} data-theme={theme}>
      <h2><FontAwesomeIcon icon={faStore} /> Inserir loja e nota fiscal</h2>
      <div className="loja-field"> {/* Reutilizada classe para consistência */}
        <label htmlFor="tipoRecebimento">Tipo de recebimento:</label>
        <select
          id="tipoRecebimento"
          value={tipoRecebimento}
          onChange={() => {}} // Vazio para evitar mudança
          className="select-input"
          disabled // Travado
        >
          <option value="Ordem de Compra">Ordem de Compra</option>
          <option value="Transferência">Transferência</option>
        </select>
      </div>
      <div className="loja-field">
        <label htmlFor="lojaEntrega">Recebimento em:</label>
        <select
          id="lojaEntrega"
          value={selectedLojaId || ''}
          onChange={handleLojaChange}
          className="select-input"
        >
          <option value="">Selecione uma loja</option>
          {lojas.map((loja) => (
            <option key={loja.id} value={loja.id}>
              {loja.nomeFantasia}
            </option>
          ))}
        </select>
      </div>
      <div className="invoice-field">
        <label htmlFor="invoiceNumber">Número da Nota Fiscal:</label>
        <input
          id="invoiceNumber"
          type="text"
          maxLength={10}
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
          className="text-input"
        />
      </div>
      <div className="form-actions">
        <SaveButton
          isLoading={isSaving}
          onClick={handleSave}
          disabled={!selectedLojaId || !invoiceNumber || isSaving}
        />
      </div>
    </div>
  );
};

export default SelectStoreAndInvoiceModal;